import { FC, Suspense, lazy } from 'react';
import { graphql, useFragment } from 'react-relay';
import { useSelector } from 'react-redux';

import { GridRow, GridCol } from 'dibs-elements/exports/Grid';
import { FormattedMessage } from 'dibs-react-intl';

import { useServerVarsContext } from '../../global/ServerVarsContext/ServerVarsContext';
import { PdpSharedDescriptionAbout } from '../PdpSharedDescriptions/PdpSharedDescriptionAbout';
import { PdpSharedDescriptionAdditionalOptions } from '../PdpSharedDescriptions/PdpSharedDescriptionAdditionalOptions';
import { PdpRespDetails } from '../PdpRespDetails/PdpRespDetails';
import { RelayVariables, Store } from '../../global/relayVariableTypes';
import { showPricingGuidance } from '../PdpSharedPricingGuidance/pricingGuidanceHelpers';

import { getAttributeInfo } from '../helpers/getAttributeInfo';

import { PdpRespAboutItemPanel_item$key } from './__generated__/PdpRespAboutItemPanel_item.graphql';
import { PdpRespAboutItemPanel_user$key } from './__generated__/PdpRespAboutItemPanel_user.graphql';

import styles from './PdpRespAboutItemPanel.scss';

const PdpRespPricingGuidanceLazy = lazy(
    () =>
        import(
            /* webpackChunkName: "PdpRespPricingGuidance" */ '../PdpRespDetails/PdpRespPricingGuidance'
        )
);

type Props = {
    showHeader?: boolean;
    item: PdpRespAboutItemPanel_item$key;
    user: PdpRespAboutItemPanel_user$key;
    currency: string;
    selectedSkuId: string | null;
    showExpandingArea?: boolean;
    dataTn?: string;
};

export const aboutHeader = (
    <FormattedMessage id="PdpRespAboutItemPanel.aboutHeader" defaultMessage="About the Item" />
);

const itemFragment = graphql`
    fragment PdpRespAboutItemPanel_item on Item {
        isCustomizable
        isDyoCustomizable
        attributes {
            customizable {
                option {
                    serviceId
                }
                text
            }
            ringResizable {
                option {
                    serviceId
                }
            }
        }
        ...PdpSharedDescriptionAbout_item
        ...PdpSharedDescriptionAdditionalOptions_item @skip(if: $isRespTabsRedesignVariant)
        ...PdpRespDetails_item
        ...PdpRespPricingGuidance_item @include(if: $isClient)
        ...pricingGuidanceHelpers_item
    }
`;

const userFragment = graphql`
    fragment PdpRespAboutItemPanel_user on User {
        ...PdpSharedDescriptionAdditionalOptions_user @skip(if: $isRespTabsRedesignVariant)
        ...PdpRespDetails_user
        ...PdpRespPricingGuidance_user @include(if: $isClient)
    }
`;

export const PdpRespAboutItemPanel: FC<Props> = ({
    showHeader,
    item: itemRef,
    user: userRef,
    currency,
    selectedSkuId,
    showExpandingArea,
    dataTn,
}) => {
    const item = useFragment(itemFragment, itemRef);
    const user = useFragment(userFragment, userRef);

    const { isTablet } = useServerVarsContext();

    const relayVariables = useSelector<Store, RelayVariables>(
        state => state.relayVariables.variables
    );

    const { isRespTabsRedesignVariant, isClient } = relayVariables;

    const { isCustomizable = null, isDyoCustomizable, attributes } = item || {};
    const { customizationOptions = null, isResizable = false } = getAttributeInfo(attributes);

    const showCustomizationOptions =
        (isCustomizable && customizationOptions) || isDyoCustomizable || isResizable;

    // For collapsed height, set const to = [# of lines to show] * [text line-height]
    const ABOUT_NUM_LINES = !isRespTabsRedesignVariant
        ? (showCustomizationOptions ? 3 : 5) * 30
        : 300;

    const showPricingGuidanceGraph = showPricingGuidance({ item, currency });

    // For details collapsed height = [# of lines to show] * ([line-height] + [margin height])
    const singleColumnDetailsHeight = 9 * (24 + 9);

    return (
        <GridRow dataTn={dataTn}>
            <GridCol
                desktop={isRespTabsRedesignVariant && showPricingGuidanceGraph ? 4 : 5}
                tabletPortrait={isRespTabsRedesignVariant && showPricingGuidanceGraph ? 6 : 12}
                tabletLandscape={
                    isRespTabsRedesignVariant && showPricingGuidanceGraph ? 6 : undefined
                }
            >
                <div className={styles.descriptionWrapper}>
                    <PdpSharedDescriptionAbout
                        item={item}
                        headerClass={showHeader ? styles.headerText : styles.isHidden}
                        contentClass={styles.paragraphText}
                        title={aboutHeader}
                        collapsedHeight={ABOUT_NUM_LINES}
                        showExpandingArea={showExpandingArea}
                    />
                </div>

                {/* Additional Options */}
                {!isRespTabsRedesignVariant && (
                    <PdpSharedDescriptionAdditionalOptions
                        buttonType="link"
                        user={user}
                        item={item}
                        headerClass={styles.headerText}
                        contentClass={styles.paragraphText}
                        ctaClass={styles.ctaWrapper}
                        showDyoVersion
                    />
                )}
            </GridCol>
            {isClient && isRespTabsRedesignVariant && isTablet && (
                <Suspense fallback="">
                    <PdpRespPricingGuidanceLazy currency={currency} item={item} user={user} />
                </Suspense>
            )}
            <GridCol
                desktop={isRespTabsRedesignVariant && showPricingGuidanceGraph ? 4 : 6}
                desktopOptions={
                    !(isRespTabsRedesignVariant && showPricingGuidanceGraph)
                        ? {
                              offset: 1,
                          }
                        : undefined
                }
                tabletPortrait={12}
                tabletLandscape={isRespTabsRedesignVariant ? 12 : undefined}
            >
                {/* Details */}
                <PdpRespDetails
                    variant={
                        isRespTabsRedesignVariant && showPricingGuidanceGraph && !isTablet
                            ? 'pdpRespTabsVariant'
                            : 'pdpRedesignVariant'
                    }
                    showHeader={false}
                    isClient={isClient}
                    currency={currency}
                    item={item}
                    selectedSkuId={selectedSkuId}
                    user={user}
                    collapsedHeight={singleColumnDetailsHeight}
                />
            </GridCol>
            {isClient && isRespTabsRedesignVariant && !isTablet && (
                <Suspense fallback="">
                    <PdpRespPricingGuidanceLazy currency={currency} item={item} user={user} />
                </Suspense>
            )}
        </GridRow>
    );
};
